import { useState, FormEvent } from 'react';
import { api } from '../lib/api';

interface ReportSummary {
  period_days: number;
  platform?: string;
  total_vulnerabilities: number;
  critical_count: number;
  high_count: number;
  medium_count: number;
  low_count: number;
  resolved_count: number;
  new_count: number;
  total_findings: number;
  scans_run: number;
  posture_score_start?: number;
  posture_score_end?: number;
  top_vulnerabilities: {
    cve_id: string;
    severity: string;
    platform: string;
    cvss_score: number;
    status: string;
    title: string;
  }[];
  recommendations: string[];
  generated_at: string;
}

const SEVERITY_COLORS: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-blue-100 text-blue-700',
};

const PERIOD_OPTIONS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '60', label: 'Last 60 days' },
  { value: '90', label: 'Last 90 days' },
];

function StatCard({ label, value, sub, color }: { label: string; value: number; sub?: string; color: string }) {
  return (
    <div className="bg-white rounded-lg shadow p-5">
      <p className={`text-2xl font-bold ${color}`}>{value.toLocaleString()}</p>
      <p className="text-sm text-gray-700 font-medium mt-0.5">{label}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

export default function ReportsPage() {
  const [period, setPeriod] = useState('30');
  const [platform, setPlatform] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<ReportSummary | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  async function generateReport(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const params = new URLSearchParams({ period_days: period });
      if (platform.trim()) params.set('platform', platform.trim());
      const data = await api.get<ReportSummary>(`/reports/summary?${params.toString()}`);
      setReport(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  }

  async function downloadPdf() {
    if (!report) return;
    setPdfLoading(true);
    try {
      const params = new URLSearchParams({ period_days: period, format: 'pdf' });
      if (platform.trim()) params.set('platform', platform.trim());
      const blob = await fetch(`${import.meta.env.VITE_API_URL ?? 'https://api.theonestack.com/api/security'}/reports/export?${params.toString()}`, {
        credentials: 'include',
      }).then(r => r.blob());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `security-report-${period}d-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to download PDF');
    } finally {
      setPdfLoading(false);
    }
  }

  const scoreDelta = report?.posture_score_end != null && report?.posture_score_start != null
    ? report.posture_score_end - report.posture_score_start
    : null;

  return (
    <div className="px-4 md:px-8 py-6 max-w-7xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Reports</h1>

      {/* Report Generator */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-base font-semibold text-gray-900 mb-4">Generate Security Report</h2>
        <form onSubmit={generateReport} className="flex flex-wrap gap-3 items-end">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Period</label>
            <select
              value={period}
              onChange={e => setPeriod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
            >
              {PERIOD_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Platform (optional)</label>
            <input
              type="text"
              value={platform}
              onChange={e => setPlatform(e.target.value)}
              placeholder="All platforms"
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 w-44"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="bg-indigo-600 text-white px-5 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
          >
            {loading ? (
              <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            ) : null}
            {loading ? 'Generating...' : 'Generate Report'}
          </button>
          {report && (
            <button
              type="button"
              onClick={downloadPdf}
              disabled={pdfLoading}
              className="border border-gray-300 text-gray-700 px-5 py-2 rounded-md text-sm font-medium hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {pdfLoading ? 'Exporting...' : 'Export PDF'}
            </button>
          )}
        </form>

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded p-3 text-sm text-red-700">{error}</div>
        )}
      </div>

      {/* Report content */}
      {report && (
        <>
          {/* Header */}
          <div className="bg-indigo-600 text-white rounded-lg shadow p-6">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-bold">Security Report</h2>
                <p className="text-indigo-200 mt-1 text-sm">
                  {PERIOD_OPTIONS.find(o => o.value === period)?.label}
                  {report.platform ? ` · ${report.platform}` : ' · All platforms'}
                </p>
              </div>
              <p className="text-indigo-200 text-xs text-right">
                Generated<br />{new Date(report.generated_at).toLocaleString()}
              </p>
            </div>
            {scoreDelta != null && (
              <div className="mt-4 flex items-center gap-2">
                <span className={`text-sm font-semibold ${scoreDelta >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                  Posture: {report.posture_score_start} → {report.posture_score_end}
                  {' '}({scoreDelta >= 0 ? '+' : ''}{scoreDelta} pts)
                </span>
              </div>
            )}
          </div>

          {/* Summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <StatCard label="Total Vulnerabilities" value={report.total_vulnerabilities} color="text-gray-900" />
            <StatCard label="Critical" value={report.critical_count} color="text-red-600" />
            <StatCard label="High" value={report.high_count} color="text-orange-500" />
            <StatCard label="Resolved" value={report.resolved_count} sub={`${report.new_count} new`} color="text-green-600" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <StatCard label="SAST/DAST Findings" value={report.total_findings} color="text-purple-600" />
            <StatCard label="Scans Run" value={report.scans_run} color="text-blue-600" />
            <StatCard label="Medium Vulnerabilities" value={report.medium_count} color="text-yellow-600" />
          </div>

          {/* Top Vulnerabilities */}
          {report.top_vulnerabilities && report.top_vulnerabilities.length > 0 && (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-base font-semibold text-gray-900">Top Vulnerabilities</h2>
              </div>
              <table className="min-w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-6 py-3">CVE</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Severity</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Platform</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">CVSS</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {report.top_vulnerabilities.map((vuln, i) => (
                    <tr key={i} className="hover:bg-gray-50">
                      <td className="px-6 py-3">
                        <p className="text-sm font-medium text-gray-900">{vuln.cve_id}</p>
                        <p className="text-xs text-gray-400 truncate max-w-xs">{vuln.title}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${SEVERITY_COLORS[vuln.severity] ?? 'bg-gray-100 text-gray-600'}`}>
                          {vuln.severity}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{vuln.platform}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{vuln.cvss_score.toFixed(1)}</td>
                      <td className="px-4 py-3">
                        <span className="text-xs capitalize text-gray-500">{vuln.status.replace(/_/g, ' ')}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Recommendations */}
          {report.recommendations && report.recommendations.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Recommendations</h2>
              <ol className="space-y-3">
                {report.recommendations.map((rec, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                      {i + 1}
                    </span>
                    <p className="text-sm text-gray-700">{rec}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </>
      )}

      {!report && !loading && (
        <div className="bg-white rounded-lg shadow p-12 text-center text-gray-400 text-sm">
          Configure the period and platform above, then click "Generate Report" to see your security summary.
        </div>
      )}
    </div>
  );
}
